import {Intent} from '@blueprintjs/core';
import {AppForm} from '@zaiusinc/app-forms';
import {Schema} from '@zaiusinc/app-forms-schema';
import {toJS} from 'mobx';
import {useObserver} from 'mobx-react-lite';
import * as queryString from 'querystring';
import React from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {fetchRemoteData} from '../../lib/fetchRemoteData';
import {filterZaiusOnlySections} from '../../lib/filterZaiusOnlySections';
import {addToQuery} from '../../lib/history';
import {AppDetailStore} from '../../stores/AppDetailStore';
import {directoryStore} from '../../stores/DirectoryStore';
import {inIframe} from '../../lib/inIframe';
import {HostPageMessenger} from '../../lib/HostPageMessenger';
import {SegmentAnalytics} from '../../lib/SegmentAnalytics';
import {toaster} from '../../lib/toaster';
import {CustomSettingsSection} from './CustomSettingsSection';
import {Opal} from './Opal/Opal';
import {CMSUIExtensions} from './CMSUIExtensions/CMSUIExtensions';

const ASSETS_BASE_URI: string = import.meta.env.VITE_ASSETS_BASE_URI || (() => {
  const url = new URL(window.location.href);
  const stem = url.host.split('.').slice(1);
  if (stem[0] === 'staging') {
    // Legacy quirks.
    return 'https://zaius-directory-staging.s3.amazonaws.com';
  }
  return 'https://apps.global.ocp.optimizely.com';
})();

interface CustomSection {
  key: string;
  title: string;
  shouldShow: () => boolean;
  render: () => React.ReactNode;
}

/**
 * Factory function to create custom sections.
 *
 * IMPORTANT: This must be called inside useObserver() for reactivity.
 * Observable properties accessed in shouldShow/render will trigger re-renders when they change.
 */
const getCustomSections = ({
  detailStore,
}: {
  detailStore: AppDetailStore;
}): CustomSection[] => [
  {
    key: 'zip_opal',
    title: 'Add to Opal',
    shouldShow: () => detailStore.hasOpal,
    render: () => <Opal detailStore={detailStore} />,
  },
  {
    key: 'cms_ui_extensions',
    title: 'Add to CMS',
    shouldShow: () => detailStore.hasCMSUIExtensions && !detailStore.hasCmsInstallationScope,
    render: () => <CMSUIExtensions detailStore={detailStore} />,
  },
];

interface Props {
  detailStore: AppDetailStore;
}

export const AppSettings = ({detailStore}: Props) => {
  const history = useHistory();
  const {search} = useLocation();
  const params = queryString.parse(search.startsWith('?') ? search.substring(1) : search);
  // Keep empty string as empty string (don't convert to undefined)
  const openSection = params.hasOwnProperty('section') ? (params['section'] as string) : undefined;

  // Check if a custom section is currently open
  const isCustomSectionOpen = (sectionKey: string) => openSection === sectionKey;

  const handleCustomSectionToggle = (sectionKey: string) => () => {
    // Set section to the custom section key to explicitly close all app form sections
    addToQuery(history, {section: sectionKey});
  };

  const handleSectionClick = (section: Schema.Section) => {
    // Close custom sections by setting a specific app form section
    addToQuery(history, {section: section.key});
  };

  return useObserver(() => {
    // IMPORTANT: Get custom sections inside useObserver to react to observable changes
    // This ensures that if detailStore.hasOpal or other observable properties change,
    // the sections list will be re-evaluated and components will re-render
    const allCustomSections = getCustomSections({detailStore});
    const visibleCustomSections = allCustomSections.filter((cs) => cs.shouldShow());
    const customSectionKeys = visibleCustomSections.map((cs) => cs.key);

    if (!detailStore.loading && params.errors) {
      detailStore.processErrors(JSON.parse(atob(params.errors as string)));
      delete params.errors;
      history.replace({...history.location, search: queryString.encode(params)});
    }

    // Only return empty if no settings AND no custom sections
    if ((detailStore.settingsFormSchema?.sections?.length || 0) === 0 && visibleCustomSections.length === 0) {
      return <div />;
    }

    const settingsFormSchema = filterZaiusOnlySections(toJS(detailStore.settingsFormSchema || {sections: []}));
    const {settingsFormData, settingsFormErrors, loading, appId, version, installId} = detailStore;

    // Determine which section to open:
    // - If openSection is a custom section key, close all app form sections
    // - If openSection has a value, use that section
    // - If openSection is undefined (no query param), use default section or first section
    let sectionToOpen: string | undefined;
    if (openSection && customSectionKeys.includes(openSection)) {
      // Explicitly close all app form sections when a custom section is open
      sectionToOpen = undefined;
    } else if (openSection) {
      // Use the section from query param
      sectionToOpen = openSection;
    } else {
      // On initial load, use default section or first visible section
      sectionToOpen = settingsFormData?.$formState?.defaultSection as string;
      if (!sectionToOpen && settingsFormSchema.sections && settingsFormSchema.sections.length > 0) {
        sectionToOpen = settingsFormSchema.sections[0].key;
      }
    }

    // Determine if a custom section should be open by default
    // This happens when:
    // 1. No explicit section is in the URL (openSection is undefined)
    // 2. No app form sections exist
    // 3. There are visible custom sections
    const hasNoAppFormSections = !settingsFormSchema.sections || settingsFormSchema.sections.length === 0;
    const shouldOpenFirstCustomSection = !openSection && hasNoAppFormSections && visibleCustomSections.length > 0;
    const defaultCustomSectionKey = shouldOpenFirstCustomSection ? visibleCustomSections[0].key : undefined;

    return (
      <div>
        <AppForm
          schema={settingsFormSchema}
          loading={loading}
          data={toJS(settingsFormData) || {}}
          options={{
            assetsBaseUrl: `${ASSETS_BASE_URI}/${encodeURIComponent(appId)}/${encodeURIComponent(String(version))}/assets`,
            controlled: true
          }}
          openSection={sectionToOpen}
          hasFeatureFlag={directoryStore.hasFeatureFlag}
          errors={toJS<Schema.FormErrors>(settingsFormErrors as any)}
          onFetchData={(section, field, source, data) => {
            return fetchRemoteData(section, field, source, data, installId!).catch((error) => {
              toaster.show({message: `${field}: ${error.message}`, intent: Intent.DANGER});
              return [];
            });
          }}
          onAction={(section, action, data) => detailStore.handleAction(section, action, data).then((resp) => {
            if (resp.redirectMode === 'url') {
              if (inIframe()) {
                HostPageMessenger.send('setWindowLocation', {url: resp.redirectTarget});
              } else {
                window.location.href = resp.redirectTarget!;
              }
            } else if (resp.redirectMode === 'settings') {
              addToQuery(history, {section: resp.redirectTarget ?? null});
            }

            const location = window.location;
            SegmentAnalytics.track('App Action', {
              page: params.tab,
              path: location.pathname + location.search,
              section,
              action,
              appId: detailStore.appId,
              appVersion: detailStore.version,
              isInstalled: detailStore.installed,
            });

            // Auto-expand first visible custom section after saving a section
            // Only expand if all conditions are met:
            // 1. The saved section is the last section in the app form (immediately before custom sections)
            // 2. No explicit redirection was sent
            // 3. No non-green toasts were sent (only 'success' or 'info' are green)
            // 4. No errors were sent
            if (visibleCustomSections.length > 0) {
              const lastAppFormSection = settingsFormSchema.sections?.[settingsFormSchema.sections.length - 1];
              const isLastSection = lastAppFormSection && section === lastAppFormSection.key;
              const hasNoRedirection = !resp.redirectMode;
              const hasOnlyGreenToasts = !resp.toasts || resp.toasts.every(
                (toast) => toast.intent === 'success' || toast.intent === 'info'
              );
              const hasNoErrors = !resp.hasErrors;
              const shouldRedirectToCustomSection =
                isLastSection &&
                hasNoRedirection &&
                hasOnlyGreenToasts &&
                hasNoErrors;

              if (shouldRedirectToCustomSection) {
                handleCustomSectionToggle(visibleCustomSections[0].key)();
              }
            }

            return resp.success;
          })}
          onSectionClick={handleSectionClick}
        />
        {visibleCustomSections.map((customSection, index) => (
          <CustomSettingsSection
            key={customSection.key}
            sectionKey={customSection.key}
            title={customSection.title}
            isOpen={isCustomSectionOpen(customSection.key) || customSection.key === defaultCustomSectionKey}
            onToggle={handleCustomSectionToggle(customSection.key)}
            showTopBorder={index === 0 && (settingsFormSchema.sections?.length || 0) > 0}
          >
            {customSection.render()}
          </CustomSettingsSection>
        ))}
      </div>
    );
  });
};
